/**
 * LE PANNEAU DES SOURCES PENNYLANE — lot 96, 18/09/2026.
 *
 * Deux questions, deux blocs :
 *
 *   - « le jeton a-t-il les droits ? » — une vérification à la demande, droit
 *     par droit, avec le périmètre à cocher chez Pennylane quand il manque.
 *     Demande du 17/09 : le jeton sera généré au dernier moment, et l'écran
 *     doit dire ce qui manque.
 *   - « que contient la copie, et de quand date-t-elle ? » — avec un bouton
 *     de lecture par source, et un pour tout relire dans l'ordre.
 *
 * LE CONTENEUR APPELLE, LA VUE DESSINE — même coupure que le rattachement,
 * pour que la vue entre dans `apercu.tsx`.
 */

import { useEffect, useState } from 'react';
import type { EchecApi } from './api.js';
import {
  lireDroitsPennylane,
  lireEtatDesSources,
  lireUnMorceauPennylane,
} from './apiAnalyse.js';
import {
  bilanDesDroits,
  fraicheur,
  lireJusquauBout,
  LIBELLES_DROIT,
  LIBELLES_SOURCES,
  ORDRE_SOURCES,
  type Avancement,
  type DroitsLus,
  type EtatSource,
  type RessourceSource,
} from './sources.js';

export function PanneauSourcesPennylane({
  jeton,
  quandAccesPerdu,
  messageRefus,
  quandLu,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly messageRefus: (cause: EchecApi) => string;
  /** Prévenu après chaque lecture terminée, pour que l'écran hôte se recalcule. */
  readonly quandLu?: () => void;
}) {
  const [etats, setEtats] = useState<readonly EtatSource[] | null>(null);
  const [droits, setDroits] = useState<DroitsLus | null>(null);
  const [verification, setVerification] = useState(false);
  const [lecture, setLecture] = useState<RessourceSource | null>(null);
  const [avancements, setAvancements] = useState<
    Partial<Record<RessourceSource, Avancement>>
  >({});
  const [refus, setRefus] = useState<string | null>(null);

  async function jetonOuSortie(): Promise<string | null> {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }

  async function chargerEtats() {
    const j = await jetonOuSortie();
    if (j === null) return;
    const r = await lireEtatDesSources(j);
    if (r.etat === 'ok') {
      setEtats(r.valeur);
    } else {
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(messageRefus(r.cause));
    }
  }

  // Une lecture de la copie à l'ouverture : elle ne sort pas de Stafa.
  useEffect(() => {
    void chargerEtats();
  }, [jeton]);

  async function verifier() {
    const j = await jetonOuSortie();
    if (j === null) return;
    setVerification(true);
    setRefus(null);
    const r = await lireDroitsPennylane(j);
    setVerification(false);
    if (r.etat === 'ok') {
      setDroits(r.valeur);
    } else {
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(messageRefus(r.cause));
    }
  }

  async function lire(quelles: readonly RessourceSource[]) {
    setRefus(null);
    for (const ressource of quelles) {
      setLecture(ressource);
      const fin = await lireJusquauBout(
        ressource,
        async (r, suite) => {
          const j = await jetonOuSortie();
          if (j === null) return null;
          const lu = await lireUnMorceauPennylane(j, r, suite);
          if (lu.etat === 'ok') return lu.valeur;
          if (lu.cause === 'acces') quandAccesPerdu();
          setRefus(messageRefus(lu.cause));
          return null;
        },
        (a) => setAvancements((x) => ({ ...x, [ressource]: a })),
      );
      // UN REFUS ARRÊTE LA SÉRIE : les dates de paiement ne se cherchent
      // pas sur des factures qu'on n'a pas pu lire.
      if (fin.issue === 'refusee' || fin.issue === 'injoignable') break;
    }
    setLecture(null);
    await chargerEtats();
    quandLu?.();
  }

  return (
    <VueSourcesPennylane
      etats={etats}
      droits={droits}
      verification={verification}
      lecture={lecture}
      avancements={avancements}
      refus={refus}
      maintenant={new Date()}
      quandVerifier={() => void verifier()}
      quandLire={(r) => void lire(r === 'tout' ? ORDRE_SOURCES : [r])}
    />
  );
}

function nombre(n: number): string {
  return new Intl.NumberFormat('fr-FR').format(n);
}

const LIBELLES_ISSUE = {
  complete: 'complète',
  partielle: 'partielle',
  refusee: 'refusée',
  injoignable: 'injoignable',
} as const;

export function VueSourcesPennylane({
  etats,
  droits,
  verification,
  lecture,
  avancements,
  refus,
  maintenant,
  quandVerifier,
  quandLire,
}: {
  readonly etats: readonly EtatSource[] | null;
  readonly droits: DroitsLus | null;
  readonly verification: boolean;
  readonly lecture: RessourceSource | null;
  readonly avancements: Partial<Record<RessourceSource, Avancement>>;
  readonly refus: string | null;
  readonly maintenant: Date;
  readonly quandVerifier: () => void;
  readonly quandLire: (r: RessourceSource | 'tout') => void;
}) {
  const occupe = lecture !== null || verification;
  return (
    <section className="sources-pennylane" aria-label="Sources Pennylane de l'analyse">
      {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}

      <div className="sources-bloc">
        <div className="sources-entete">
          <div>
            <h3>Droits du jeton</h3>
            <p className="sources-aide">
              Ce que l’analyse financière a le droit de lire chez Pennylane.
            </p>
          </div>
          <button
            type="button"
            className="bouton bouton-discret"
            disabled={occupe}
            onClick={quandVerifier}
          >
            {verification ? 'Vérification…' : 'Vérifier les droits'}
          </button>
        </div>
        {droits === null ? (
          <p className="sources-vide">Droits non vérifiés.</p>
        ) : (
          <>
            <p
              className={
                droits.droits.every((d) => d.etat === 'ok')
                  ? 'bandeau bandeau-fait'
                  : 'bandeau bandeau-attente'
              }
            >
              {bilanDesDroits(droits)}
            </p>
            <div className="sources-defile">
              <table className="sources-table">
                <thead>
                  <tr>
                    <th>Donnée</th>
                    <th>Périmètre</th>
                    <th>Sert à</th>
                    <th>État</th>
                  </tr>
                </thead>
                <tbody>
                  {droits.droits.map((d) => (
                    <tr key={d.cle}>
                      <td>{d.libelle}</td>
                      <td className="sources-code">{d.perimetre}</td>
                      <td className="sources-secondaire">{d.sert_a}</td>
                      <td>
                        <span className={`sources-etat etat-${d.etat}`}>
                          {LIBELLES_DROIT[d.etat]}
                          {d.statut !== null && d.etat !== 'ok'
                            ? ` (${String(d.statut)})`
                            : ''}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="sources-bloc">
        <div className="sources-entete">
          <div>
            <h3>Copies lues</h3>
            <p className="sources-aide">
              Stafa calcule sur une copie. Relisez-la avant une analyse.
            </p>
          </div>
          <button
            type="button"
            className="bouton"
            disabled={occupe}
            onClick={() => quandLire('tout')}
          >
            {lecture !== null ? 'Lecture en cours…' : 'Tout relire'}
          </button>
        </div>
        {etats === null ? (
          <p className="attente">Lecture de la copie…</p>
        ) : (
          <div className="sources-defile">
            <table className="sources-table">
              <thead>
                <tr>
                  <th>Donnée</th>
                  <th className="nombre">Lignes</th>
                  <th>Lue</th>
                  <th>Dernière lecture</th>
                  <th aria-label="Action" />
                </tr>
              </thead>
              <tbody>
                {etats.map((e) => {
                  const a = avancements[e.ressource];
                  const enCours = lecture === e.ressource;
                  return (
                    <tr key={e.ressource} className={enCours ? 'sources-en-cours' : ''}>
                      <td>{LIBELLES_SOURCES[e.ressource]}</td>
                      <td className="nombre">
                        {nombre(e.lignes)}
                        {e.restantes !== null && e.restantes > 0 && (
                          <span className="sources-secondaire sources-reste">
                            {nombre(e.restantes)} à chercher
                          </span>
                        )}
                      </td>
                      <td>{fraicheur(e.lu_le, maintenant)}</td>
                      <td>
                        {a !== undefined ? (
                          <span className={`sources-issue issue-${a.issue}`}>
                            {a.termine
                              ? `${LIBELLES_ISSUE[a.issue]} · ${nombre(a.lignes)} ligne(s)`
                              : `en cours · ${nombre(a.lignes)} ligne(s)`}
                            {a.ecartees > 0
                              ? ` · ${nombre(a.ecartees)} écartée(s)`
                              : ''}
                            {a.message !== null && (
                              <span className="sources-message">{a.message}</span>
                            )}
                          </span>
                        ) : e.derniere !== null ? (
                          <span className={`sources-issue issue-${e.derniere.issue}`}>
                            {LIBELLES_ISSUE[e.derniere.issue]} ·{' '}
                            {fraicheur(e.derniere.lance_le, maintenant)} ·{' '}
                            {e.derniere.lance_par}
                            {e.derniere.message !== null && (
                              <span className="sources-message">
                                {e.derniere.message}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="sources-secondaire">aucune</span>
                        )}
                      </td>
                      <td className="sources-action">
                        <button
                          type="button"
                          className="bouton bouton-discret bouton-petit"
                          disabled={occupe}
                          onClick={() => quandLire(e.ressource)}
                        >
                          Lire
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
